import {
  BATCH_TYPE_TOTAL_WT,
  ingredientDryYeast,
  ingredientFlour,
  ingredientLevain,
  ingredientUnknown,
  ingredientWater, LEVAIN_PCT_TYPE_PFF,
  LEVAIN_PCT_TYPE_TOTAL_WT,
  noPreferment, SUBMIX_TYPE_PORRIDGE, SUBMIX_TYPE_SCALD, SUBMIX_TYPE_SOAKER
} from "../shared/Types";
import {convertUnitToGrams} from "../shared/Units";
import {toFixedDecimal, toFixedWholePercent} from "../utils/NumberUtils";
import {deriveRatioIdFromMultiplier, yeastDefs} from "../shared/Yeast";
import {getWeightDistribution} from "../dough/CalculatorUtils";

// REMINDER: The data structure used in this converter must mimic the input form data structure
// maintained by both the ratio and weight form screens.
// i.e. the "mainDough" node is flattened out at the top and "prefermentDough" is called "prefermentData"
// This allows the output of the converter to be sent directly to the ratio form as session data.
// In hindsight, I should not have two slightly different data structures...
// The form input data structure was optimized for space as it's stored in local storage and in cloud db,
// whereas the calculators were revised for clarity and better organization of data.
const ratioFormItems = {
  formName: ``,
  formType: '',
  attribution: '',
  createdAt: '',
  modifiedAt: '',
  numBatches: 1,
  batchSize: 0,
  batchType: BATCH_TYPE_TOTAL_WT,
  hydrationPct: 0,
  process: {},
  pffBakersPct: false,
  prefermentPffPct: 0,
  prefermentPffPctType: LEVAIN_PCT_TYPE_TOTAL_WT,
  starterType: LEVAIN_PCT_TYPE_TOTAL_WT,
  starterPct: 0,
  starterHydrationPct: 0,
  starterFlourWeight: 0,
  starterLiquidWeight: 0,
  starterFlourPercent: 0,
  starterLiquidPercent: 0,
  yeastRatioId: 0,
  includePreferment: false,
  effectiveRecipeFlourTotalWeight: 0,
  bakersPercentFlourWeight: 0,
  recipeHydrationPct: 0,
  effectiveRecipeHydrationPct: 0,
  flour: [],
  liquid: [],
  mixin: [],
  submixes: [],
  toppings: [],
  notes: '',
  prefermentData: {
    addBuffer: false,
    prefermentType: noPreferment,
    hydrationPct: 0,
    yeastRatioId: 0,
    starterType: LEVAIN_PCT_TYPE_TOTAL_WT,
    starterPct: 0,
    starterHydrationPct: 0,
    flourEffectiveWeight: 0,
    flour: [],
    liquid: [],
    mixin: []
  }
}

function convertToRatioForm(inputData) {
  let items = JSON.parse(JSON.stringify(ratioFormItems))
  items.formName = inputData.formName
  items.formType = inputData.formType
  items.numBatches = inputData.numBatches
  items.attribution = inputData.attribution
  items.createdAt = inputData.createdAt
  items.modifiedAt = inputData.modifiedAt
  items.notes = inputData.notes
  items.process = inputData.process ?? {}
  items.includePreferment = inputData.includePreferment
  items.starterHydrationPct = inputData.starterHydrationPct
  if (inputData.includePreferment) {
    items.prefermentData.prefermentType = inputData.prefermentData.prefermentType
    items.prefermentData.starterHydrationPct = inputData.prefermentData.starterHydrationPct
  } else {
    items.prefermentData = undefined
  }
  items.labels = []
  items.labels.push.apply(items.labels, inputData.labels)
  items.pffBakersPct = inputData.pffBakersPct
  items.prefermentPffPctType = inputData.prefermentPffPctType
  return calculateRecipePercentages(items, inputData)
}

function calculateRecipePercentages(items, inputData) {
  // Note that we need to calculate weights in order to get percentages, but we're
  // not storing weights in the output structure to keep the purity of the _ratio_ structure which is also
  // used when sending to the ratio (%Bakers) formula session data
  let mainDoughFlourWeight = 0
  // get main flour total weight
  inputData.flour.forEach((fd, ix) => {
    mainDoughFlourWeight += convertUnitToGrams(fd.amount, fd.unit, ingredientFlour)
  })

  let mainDoughLiquidWeight = 0
  // get main liquid total weight
  inputData.liquid.forEach((fd, ix) => {
    const liquidGrams = convertUnitToGrams(fd.amount, fd.unit, ingredientWater)
    mainDoughLiquidWeight += liquidGrams
  })

  let mainEffLevainFlourWeight = 0
  let mainEffLevainLiquidWeight = 0
  const mainLevainWeight = convertUnitToGrams(inputData.starterAmount, inputData.starterUnit, ingredientLevain)
  if (mainLevainWeight > 0) {
    mainEffLevainFlourWeight = Math.round(mainLevainWeight / 2)   // assume 100% levain hydration for MAIN levain
    mainEffLevainLiquidWeight = (mainLevainWeight - mainEffLevainFlourWeight)  // 100% hydration, equal parts flour/water
  }
  items.starterFlourWeight = mainEffLevainFlourWeight
  items.starterLiquidWeight = mainEffLevainLiquidWeight
  items.starterFlourPercent = Math.round(items.starterPct * (mainEffLevainFlourWeight / mainLevainWeight))
  items.starterLiquidPercent = Math.round(items.starterPct * (mainEffLevainLiquidWeight / mainLevainWeight))

  // consoleLog(`mainDoughFlourWeight=${mainDoughFlourWeight}, mainDoughLiquidWeight=${mainDoughLiquidWeight}, mainLevainWeight: ${mainLevainWeight}`)

  let prefermentFlourWeight = 0
  let prefermentLiquidWeight = 0
  let prefermentMixinWeight = 0
  let prefermentStarterWeight = 0
  let prefermentEffStarterFlourWeight = 0
  let prefermentEffStarterLiquidWeight = 0
  let prefermentEffFlourWeight = 0
  let prefermentEffLiquidWeight = 0
  let prefermentTotalWeight = 0
  if (inputData.includePreferment) {
    // get preferment flour total
    inputData.prefermentData.flour.forEach((fd, ix) => {
      prefermentFlourWeight += convertUnitToGrams(fd.amount, fd.unit, ingredientFlour)
    })
    // push preferment flour with percentages
    inputData.prefermentData.flour.forEach((fd, ix) => {
      const flourGrams = convertUnitToGrams(fd.amount, fd.unit, ingredientFlour)
      const flourPercent = toFixedWholePercent(flourGrams / prefermentFlourWeight)
      items.prefermentData.flour.push({id: fd.id, name: fd.name, percent: flourPercent, percentStr: `${flourPercent}` })
      // consoleLog(`preferment flour: ${fd.name}, grams: ${flourGrams}, percent: ${flourPercent}`)
    })
    prefermentStarterWeight = convertUnitToGrams(inputData.prefermentData.starterAmount, inputData.prefermentData.starterUnit, ingredientLevain)
    if (prefermentStarterWeight > 0) {
      const dist = getWeightDistribution(prefermentStarterWeight, (inputData.prefermentData.starterHydrationPct / 100))
      prefermentEffStarterFlourWeight = dist.flourWeight
      prefermentEffStarterLiquidWeight = dist.liquidWeight
      // effective flour weight, used to meet overall preferment hydro%
      prefermentEffFlourWeight = prefermentFlourWeight + prefermentEffStarterFlourWeight
    }
    // get total liquid weight
    inputData.prefermentData.liquid.forEach((fd, ix) => {
      const liquidGrams = convertUnitToGrams(fd.amount, fd.unit, ingredientWater)
      prefermentLiquidWeight += liquidGrams
    })
    // effective liquid weight, used to meet overall preferment hydro%
    prefermentEffLiquidWeight = prefermentLiquidWeight + prefermentEffStarterLiquidWeight

    // calculate percentages in terms of total liquid
    inputData.prefermentData.liquid.forEach((fd, ix) => {
      const liquidGrams = convertUnitToGrams(fd.amount, fd.unit, ingredientWater)
      const liquidPercent = toFixedWholePercent(liquidGrams / prefermentLiquidWeight)
      items.prefermentData.liquid.push({id: fd.id, name: fd.name, percent: liquidPercent, percentStr: `${liquidPercent}` })
      // consoleLog(`preferment liquid: ${fd.name}, grams: ${liquidGrams}, percent: ${liquidPercent}`)
    })
    // preferment mixin with percentages
    inputData.prefermentData.mixin.forEach((md, ix) => {
      const mixinGrams = convertUnitToGrams(md.amount, md.unit, ingredientUnknown)
      const mixinPercent = toFixedWholePercent(mixinGrams / prefermentEffFlourWeight)
      items.prefermentData.mixin.push({id: md.id, name: md.name, percent: mixinPercent })
      // consoleLog(`preferment mixin: ${md.name}, grams: ${mixinGrams}, percent: ${mixinPercent}`)
      prefermentMixinWeight += mixinGrams
    })
    prefermentTotalWeight = prefermentFlourWeight + prefermentLiquidWeight + prefermentStarterWeight + prefermentMixinWeight
  }


  const recipeFlourWeight = mainDoughFlourWeight + prefermentFlourWeight
  const recipeLiquidWeight = mainDoughLiquidWeight + prefermentLiquidWeight
  const effectiveRecipeFlourWeight = recipeFlourWeight + mainEffLevainFlourWeight + prefermentEffStarterFlourWeight
  const effectiveRecipeLiquidWeight = recipeLiquidWeight + mainEffLevainLiquidWeight + prefermentEffStarterLiquidWeight
  const bakersPercentFlourWeight = items.pffBakersPct ? effectiveRecipeFlourWeight : mainDoughFlourWeight

  // consoleLog(`effectiveRecipeFlourWeight=${effectiveRecipeFlourWeight}, effectiveRecipeLiquidWeight=${effectiveRecipeLiquidWeight}`)
  items.effectiveRecipeFlourTotalWeight = effectiveRecipeFlourWeight
  items.effectiveRecipeLiquidTotalWeight = effectiveRecipeLiquidWeight
  items.effectiveRecipeHydrationPct = toFixedDecimal(100 * effectiveRecipeLiquidWeight / effectiveRecipeFlourWeight)
  items.recipeHydrationPct = items.pffBakersPct ? items.effectiveRecipeHydrationPct : toFixedDecimal(100 * (mainDoughLiquidWeight / mainDoughFlourWeight))
  items.bakersPercentFlourWeight = bakersPercentFlourWeight
  items.hydrationPct = items.recipeHydrationPct

  // consoleLog(`recipe hydration: ${items.hydrationPct}% `)

  // now ready to calculate main flour and liquid percentages, since we have determined the appropriate flour
  // weight to use...
  // push main dough flour with percentages
  inputData.flour.forEach((fd, ix) => {
    const flourGrams = convertUnitToGrams(fd.amount, fd.unit, ingredientFlour)
    const flourPercent = toFixedWholePercent(flourGrams / bakersPercentFlourWeight)
    items.flour.push({id: fd.id, name: fd.name, percent: flourPercent, percentStr: `${flourPercent}` })
    // consoleLog(`main flour: ${fd.name}, grams: ${flourGrams}, percent: ${flourPercent}`)
  })

  // calculate percentages in terms of total liquid
  inputData.liquid.forEach((fd, ix) => {
    const liquidGrams = convertUnitToGrams(fd.amount, fd.unit, ingredientWater)
    const liquidPercent = toFixedWholePercent(liquidGrams / bakersPercentFlourWeight)
    items.liquid.push({id: fd.id, name: fd.name, percent: liquidPercent, percentStr: `${liquidPercent}` })
    // consoleLog(`main liquid: ${fd.name}, grams: ${liquidGrams}, percent: ${liquidPercent}`)
  })

  let totalSubmixWeight = 0
  totalSubmixWeight += applyPorridgeSubmix(items, inputData, bakersPercentFlourWeight)
  totalSubmixWeight += applyScaldSubmix(items, inputData, bakersPercentFlourWeight)
  totalSubmixWeight += applySoakerSubmix(items, inputData, bakersPercentFlourWeight)

  // push main dough mixin with percentages
  let mainMixinWeight = 0
  inputData.mixin.forEach((md, ix) => {
    const mixinGrams = convertUnitToGrams(md.amount, md.unit, ingredientUnknown)
    const mixinPercent = toFixedWholePercent(mixinGrams / bakersPercentFlourWeight)
    items.mixin.push({id: md.id, name: md.name, percent: mixinPercent })
    // consoleLog(`mixin: ${md.name}, grams: ${mixinGrams}, percent: ${mixinPercent}`)
    mainMixinWeight += mixinGrams
  })
  // toppings - just another category of mixin but weight isn't included
  inputData.toppings.forEach((td, ix) => {
    items.toppings.push({id: td.id, name: td.name, percent: 0 })
    // consoleLog(`topping: ${td.name}, grams: 0, percent: 0`)
  })
  // consoleLog(`total mixin grams: ${totalMixinWeight}`)

  items.starterPct = toFixedWholePercent(mainLevainWeight / bakersPercentFlourWeight)

  // main yeast
  const mainYeastWeight = convertUnitToGrams(inputData.yeastAmount, inputData.yeastUnit, ingredientDryYeast)
  //  yw = um * ru * (fw/100)
  //  um = ym / (ru * (fw/100))
  //  where yw = yeast weight, um = unit multiplier, ru = ratio unit grams, fw = flour weight
  const mainYeastUnitMultiplier = (mainYeastWeight / (yeastDefs.yeastRatioUnitGrams * (bakersPercentFlourWeight / 100)))
  items.yeastRatioId = deriveRatioIdFromMultiplier(mainYeastUnitMultiplier)
  items.yeastPct = toFixedWholePercent(mainYeastWeight / bakersPercentFlourWeight)
  // consoleLog(`main yeast grams: ${mainYeastWeight}, yeastRatioId: ${items.yeastRatioId}, yeastPct: ${items.yeastPct}`)

  // preferment totals
  let prefermentYeastWeight = 0
  if (inputData.includePreferment) {
    if (inputData.prefermentPffPctType === LEVAIN_PCT_TYPE_PFF) {
      items.prefermentPffPct = toFixedWholePercent((prefermentFlourWeight + prefermentEffStarterFlourWeight) / bakersPercentFlourWeight)
    } else {
      items.prefermentPffPct = toFixedWholePercent(prefermentTotalWeight / bakersPercentFlourWeight)
    }
    // New: preferment hydrationPct needs to account for starter flour and hydration
    items.prefermentData.hydrationPct = toFixedWholePercent(prefermentEffLiquidWeight / prefermentEffFlourWeight)
    items.prefermentData.starterPct = toFixedWholePercent(prefermentStarterWeight / prefermentFlourWeight)

    prefermentYeastWeight = convertUnitToGrams(inputData.prefermentData.yeastAmount, inputData.prefermentData.yeastUnit, ingredientDryYeast)
    //  yw = um * ru * (fw/100)
    //  um = ym / (ru * (fw/100))
    //  where yw = yeast weight, um = unit multiplier, ru = ratio unit grams, fw = flour weight
    const prefermentYeastUnitMultiplier = (prefermentYeastWeight / (yeastDefs.yeastRatioUnitGrams * (prefermentFlourWeight / 100)))
    items.prefermentData.yeastRatioId = deriveRatioIdFromMultiplier(prefermentYeastUnitMultiplier)
    items.prefermentData.yeastPct = toFixedWholePercent(prefermentYeastWeight / prefermentFlourWeight)
    items.prefermentData.flourEffectiveWeight = prefermentEffFlourWeight
    // consoleLog(`preferment yeast grams: ${prefermentYeastWeight}, yeastRatioId: ${items.prefermentData.yeastRatioId}`)
    // consoleLog(`preferment hydrationPct: ${items.prefermentData.hydrationPct}`)
    // consoleLog(`prefermentPffPct: ${items.prefermentPffPct}`)
  }

  // total weight - all ingredients!
  items.batchSize = Math.round((mainDoughFlourWeight + mainDoughLiquidWeight + prefermentFlourWeight + prefermentLiquidWeight
          + mainMixinWeight + prefermentMixinWeight + totalSubmixWeight + mainLevainWeight + mainYeastWeight + prefermentStarterWeight + prefermentYeastWeight) / items.numBatches)
  // consoleLog(`recipe total batch size: ${items.batchSize}`)
  // consoleLog(`ratioConverter produces items: ${JSON.stringify(items)}`)
  return items
}

function getSubmixWeightGrams(submixIngredientArr, ingredientType) {
  let weightGrams = 0
  submixIngredientArr?.forEach((si) => {
    weightGrams += convertUnitToGrams(si['amount'], si['unit'], ingredientType)
  })
  return weightGrams
}

// calculate weights for each porridge ingredient
function applyPorridgeSubmix(items, inputData, recipeFlourWeight) {
  let submixSumWeight = 0
  inputData.submixes?.filter((sm) => sm.type === SUBMIX_TYPE_PORRIDGE).forEach((sm, ix) => {
    const submixGrainWeight = getSubmixWeightGrams(sm.grain, ingredientUnknown)
    const submixGrainIngredients = []
    // flour items of submix should total 100%
    sm.grain.forEach((smg) => {
      const grainGrams = convertUnitToGrams(smg.amount, smg.unit, ingredientUnknown)
      const grainPercent = toFixedWholePercent(grainGrams / submixGrainWeight)
      submixGrainIngredients.push({id: smg.id, name: smg.name, percent: grainPercent, percentStr: `${grainPercent}`,
        weight: grainGrams, group: smg.group, altUnit: smg.unit, altUnitAmount: smg.amount})
    })

    // grain ingredients are the basis for the other ingredients' percentages
    const submixDryIngredients = []
    let submixDryWeight = 0
    sm.dry?.forEach((smi) => {
      const dryGrams = convertUnitToGrams(smi.amount, smi.unit, ingredientUnknown)
      const dryPercent = toFixedWholePercent(dryGrams / submixGrainWeight)
      submixDryWeight += dryGrams
      submixDryIngredients.push({id: smi.id, name: smi.name, percent: dryPercent, percentStr: `${dryPercent}`,
        weight: dryGrams, group: smi.group, altUnit: smi.unit, altUnitAmount: smi.amount})
    })

    const submixLiquidIngredients = []
    let submixLiquidWeight = 0
    sm.liquid.forEach((sml) => {
      const liquidGrams = convertUnitToGrams(sml.amount, sml.unit, ingredientWater)
      const liquidPercent = toFixedWholePercent(liquidGrams / submixGrainWeight)
      submixLiquidWeight += liquidGrams
      submixLiquidIngredients.push({id: sml.id, name: sml.name, percent: liquidPercent, percentStr: `${liquidPercent}`,
        weight: liquidGrams, group: sml.group, altUnit: sml.unit, altUnitAmount: sml.amount})
    })

    const submixTotalWeight = submixGrainWeight + submixDryWeight + submixLiquidWeight
    const submixMainPct = toFixedWholePercent((submixTotalWeight / recipeFlourWeight), 2)
    const absorbPct = toFixedWholePercent(((submixTotalWeight - submixGrainWeight - submixDryWeight) / (submixGrainWeight + submixDryWeight)), 2)

    items.submixes.push({id: sm.id, type: sm.type, name: sm.name, mainPct: submixMainPct, absorbPct: absorbPct,
      notes: sm.notes, weight: submixTotalWeight, grain: submixGrainIngredients,
      liquid: submixLiquidIngredients, dry: submixDryIngredients})
    submixSumWeight += submixTotalWeight
  })
  return submixSumWeight
}

// calculate weights for each scald ingredient
function applyScaldSubmix(items, inputData, recipeFlourWeight) {
  let submixSumWeight = 0
  inputData.submixes?.filter((sm) => sm.type === SUBMIX_TYPE_SCALD).forEach((sm, ix) => {
    const submixFlourWeight = getSubmixWeightGrams(sm.flour, ingredientFlour)
    const submixFlourIngredients = []
    // flour items of submix should total 100%
    sm.flour?.forEach((smf) => {
      const flourGrams = convertUnitToGrams(smf.amount, smf.unit, ingredientUnknown)
      const flourPercent = toFixedWholePercent(flourGrams / submixFlourWeight)
      submixFlourIngredients.push({id: smf.id, name: smf.name, percent: flourPercent, percentStr: `${flourPercent}`,
        weight: flourGrams, group: smf.group, altUnit: smf.unit, altUnitAmount: smf.amount})
    })

    const submixLiquidIngredients = []
    let submixLiquidWeight = 0
    sm.liquid.forEach((sml) => {
      const liquidGrams = convertUnitToGrams(sml.amount, sml.unit, ingredientWater)
      const liquidPercent = toFixedWholePercent(liquidGrams / submixFlourWeight)
      submixLiquidWeight += liquidGrams
      submixLiquidIngredients.push({id: sml.id, name: sml.name, percent: liquidPercent, percentStr: `${liquidPercent}`,
        weight: liquidGrams, group: sml.group, altUnit: sml.unit, altUnitAmount: sml.amount})
    })

    const submixTotalWeight = submixFlourWeight + submixLiquidWeight
    const submixMainPct = toFixedWholePercent((submixTotalWeight / recipeFlourWeight), 2)

    items.submixes.push({id: sm.id, type: sm.type, name: sm.name, mainPct: submixMainPct,
      notes: sm.notes, weight: submixTotalWeight, flour: submixFlourIngredients, liquid: submixLiquidIngredients})
    submixSumWeight += submixTotalWeight
  })
  return submixSumWeight
}

// calculate weights for each soaker ingredient
function applySoakerSubmix(items, inputData, recipeFlourWeight) {
  let submixSumWeight = 0
  inputData.submixes?.filter((sm) => sm.type === SUBMIX_TYPE_SOAKER).forEach((sm, ix) => {
    const submixDryWeight = getSubmixWeightGrams(sm.dry, ingredientUnknown)
    const submixDryIngredients = []
    sm.dry?.forEach((smi) => {
      const dryGrams = convertUnitToGrams(smi.amount, smi.unit, ingredientUnknown)
      const dryPercent = toFixedWholePercent(dryGrams / submixDryWeight)
      submixDryIngredients.push({id: smi.id, name: smi.name, percent: dryPercent, percentStr: `${dryPercent}`,
        weight: dryGrams, group: smi.group, altUnit: smi.unit, altUnitAmount: smi.amount})
    })

    const submixLiquidIngredients = []
    sm.liquid.forEach((sml) => {
      const liquidGrams = convertUnitToGrams(sml.amount, sml.unit, ingredientWater)
      const liquidPercent = toFixedWholePercent(liquidGrams / submixDryWeight)
      submixLiquidIngredients.push({id: sml.id, name: sml.name, percent: liquidPercent, percentStr: `${liquidPercent}`,
        weight: liquidGrams, group: sml.group, altUnit: sml.unit, altUnitAmount: sml.amount})
    })

    const submixTotalWeight = convertUnitToGrams(sm.mainAmount, sm.mainAmountUnit, ingredientUnknown)  // assume liquid is drained
    const submixMainPct = toFixedWholePercent((submixTotalWeight / recipeFlourWeight), 2)
    const absorbPct = toFixedWholePercent(((submixTotalWeight - submixDryWeight) / submixDryWeight),2)
    items.submixes.push({id: sm.id, type: sm.type, name: sm.name, mainPct: submixMainPct, absorbPct: absorbPct,
      notes: sm.notes, weight: submixTotalWeight, dry: submixDryIngredients, liquid: submixLiquidIngredients})
    submixSumWeight += submixTotalWeight
  })
  return submixSumWeight
}

function consoleLog(message) {
  console.log(message)
}

export {convertToRatioForm}